import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Shield, Crown, Feather, TreePine, Sparkles, Users, 
  Palette, Download, Share, Eye, Star, Heart, Mountain,
  Sword, Book, Anchor, Sun, Moon, Bird, Zap, 
  Deer, Fish, Flower, Leaf, Flame, Waves, 
  Plus, Minus, RotateCcw, Wand2, Image as ImageIcon
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { crestAPI, publicProfileAPI } from '../services/api';
import AuthModal from '../components/AuthModal';
import useSubscription from '../hooks/useSubscription';

const CrestGenerator = () => {
  const { isAuthenticated, user } = useAuth();
  const { isPaidUser } = useSubscription();
  const { toast } = useToast();
  
  // Crest customization state
  const [crestConfig, setCrestConfig] = useState({
    // Basic Information
    familyName: '',
    motto: '',
    tribalAffiliation: '',
    
    // Crest Shape
    shape: 'traditional', // traditional, modern, circular, diamond, tribal
    
    // Primary Symbols (user can select multiple)
    primarySymbols: [],
    
    // Animals & Nature
    animals: [],
    plants: [],
    naturalElements: [],
    
    // Cultural Elements
    culturalSymbols: [],
    
    // Colors (main palette)
    primaryColor: '#8B4513',   // Brown
    secondaryColor: '#228B22', // Forest Green
    accentColor: '#FFD700',    // Gold
    backgroundStyle: 'solid',  // solid, gradient, textured
    
    // Style Preferences
    artStyle: 'traditional',   // traditional, modern, minimalist, detailed
    complexity: 'medium',      // simple, medium, complex
    
    // Additional Details
    includeSpiritual: false,
    includeBanner: true,
    includeStars: false,
    includeBorder: true,
    
    // Generation settings
    isGenerating: false,
    generatedCrest: null,
    generationStep: ''
  });

  // Symbol options
  const shapeOptions = [
    { value: 'traditional', label: 'Traditional Shield', icon: Shield },
    { value: 'modern', label: 'Modern Shield', icon: Shield },
    { value: 'circular', label: 'Circular Crest', icon: Sun },
    { value: 'diamond', label: 'Diamond Shape', icon: Star },
    { value: 'tribal', label: 'Tribal Design', icon: Feather }
  ];

  const primarySymbolOptions = [
    { value: 'crown', label: 'Crown (Leadership)', icon: Crown, meaning: 'Authority, leadership, sovereignty' },
    { value: 'feather', label: 'Sacred Feather', icon: Feather, meaning: 'Spiritual connection, honor, courage' },
    { value: 'tree', label: 'Tree of Life', icon: TreePine, meaning: 'Growth, strength, family roots' },
    { value: 'star', label: 'Guiding Star', icon: Star, meaning: 'Direction, hope, destiny' },
    { value: 'heart', label: 'Sacred Heart', icon: Heart, meaning: 'Love, compassion, life force' },
    { value: 'mountain', label: 'Mountain', icon: Mountain, meaning: 'Strength, permanence, wisdom' },
    { value: 'sword', label: 'Warrior Sword', icon: Sword, meaning: 'Protection, justice, honor' },
    { value: 'book', label: 'Book of Knowledge', icon: Book, meaning: 'Wisdom, learning, heritage' },
    { value: 'anchor', label: 'Anchor', icon: Anchor, meaning: 'Stability, hope, faithfulness' }
  ];

  const animalOptions = [
    { value: 'eagle', label: 'Eagle', icon: Zap, meaning: 'Freedom, strength, spiritual connection' },
    { value: 'wolf', label: 'Wolf', icon: Heart, meaning: 'Loyalty, family bonds, intelligence' },
    { value: 'bear', label: 'Bear', icon: Shield, meaning: 'Courage, protection, healing' },
    { value: 'deer', label: 'Deer', icon: TreePine, meaning: 'Gentleness, grace, sensitivity' },
    { value: 'bird', label: 'Sacred Bird', icon: Bird, meaning: 'Messenger, freedom, transcendence' },
    { value: 'fish', label: 'Fish', icon: Fish, meaning: 'Abundance, knowledge, transformation' }
  ];

  const plantOptions = [
    { value: 'oak', label: 'Oak Tree', icon: TreePine, meaning: 'Strength, endurance, wisdom' },
    { value: 'cedar', label: 'Cedar', icon: TreePine, meaning: 'Purification, protection, healing' },
    { value: 'sage', label: 'Sacred Sage', icon: Leaf, meaning: 'Cleansing, wisdom, respect' },
    { value: 'flower', label: 'Sacred Flower', icon: Flower, meaning: 'Beauty, life cycle, renewal' },
    { value: 'corn', label: 'Corn/Maize', icon: Leaf, meaning: 'Sustenance, fertility, survival' },
    { value: 'tobacco', label: 'Sacred Tobacco', icon: Leaf, meaning: 'Prayer, offering, communication' }
  ];

  const naturalElementOptions = [
    { value: 'sun', label: 'Sun', icon: Sun, meaning: 'Life, energy, renewal' },
    { value: 'moon', label: 'Moon', icon: Moon, meaning: 'Cycles, intuition, feminine power' },
    { value: 'fire', label: 'Sacred Fire', icon: Flame, meaning: 'Transformation, spirit, warmth' },
    { value: 'water', label: 'Water', icon: Waves, meaning: 'Life, purification, flow' },
    { value: 'earth', label: 'Earth', icon: Mountain, meaning: 'Foundation, nurturing, stability' },
    { value: 'wind', label: 'Wind', icon: Feather, meaning: 'Change, breath of life, communication' }
  ];

  const culturalSymbolOptions = [
    { value: 'dreamcatcher', label: 'Dreamcatcher', icon: Sun, meaning: 'Protection, filtering negative energy' },
    { value: 'medicine_wheel', label: 'Medicine Wheel', icon: Sun, meaning: 'Balance, seasons, life cycles' },
    { value: 'arrow', label: 'Arrow', icon: Sword, meaning: 'Direction, protection, hunting' },
    { value: 'thunderbird', label: 'Thunderbird', icon: Zap, meaning: 'Power, protection, strength' },
    { value: 'turtle', label: 'Turtle', icon: Shield, meaning: 'Mother Earth, longevity, wisdom' },
    { value: 'spiral', label: 'Sacred Spiral', icon: Sun, meaning: 'Life journey, growth, eternity' }
  ];

  const colorOptions = [
    { value: '#8B4513', label: 'Earth Brown', meaning: 'Grounding, stability, earth connection' },
    { value: '#228B22', label: 'Forest Green', meaning: 'Nature, growth, harmony' },
    { value: '#B22222', label: 'Sacred Red', meaning: 'Life force, courage, passion' },
    { value: '#191970', label: 'Deep Blue', meaning: 'Water, sky, spiritual depth' },
    { value: '#FFD700', label: 'Golden Yellow', meaning: 'Sun, wisdom, enlightenment' },
    { value: '#800080', label: 'Royal Purple', meaning: 'Spirituality, nobility, mystery' },
    { value: '#FF8C00', label: 'Sacred Orange', meaning: 'Energy, creativity, warmth' },
    { value: '#2F4F4F', label: 'Stone Gray', meaning: 'Balance, neutrality, wisdom' },
    { value: '#000000', label: 'Deep Black', meaning: 'Protection, power, elegance' },
    { value: '#FFFFFF', label: 'Pure White', meaning: 'Purity, peace, new beginnings' }
  ];

  const handleSymbolToggle = (category, value) => {
    setCrestConfig(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(item => item !== value)
        : [...prev[category], value]
    }));
  };

  const generateCrest = async () => {
    if (!isAuthenticated) {
      toast({
        variant: "destructive",
        title: "Authentication Required",
        description: "Please sign in to generate your tribal crest.",
      });
      return;
    }

    // Validate minimum requirements
    if (!crestConfig.familyName.trim()) {
      toast({
        variant: "destructive",
        title: "Family Name Required",
        description: "Please enter your family name for the crest.",
      });
      return;
    }

    if (crestConfig.primarySymbols.length === 0) {
      toast({
        variant: "destructive",
        title: "Select Primary Symbol",
        description: "Please select at least one primary symbol for your crest.",
      });
      return;
    }

    try {
      setCrestConfig(prev => ({
        ...prev,
        isGenerating: true,
        generationStep: 'Preparing your crest design...'
      }));

      // Prepare crest generation request
      const generationRequest = {
        family_name: crestConfig.familyName,
        motto: crestConfig.motto,
        tribal_affiliation: crestConfig.tribalAffiliation,
        shape: crestConfig.shape,
        primary_symbols: crestConfig.primarySymbols,
        animals: crestConfig.animals,
        plants: crestConfig.plants,
        natural_elements: crestConfig.naturalElements,
        cultural_symbols: crestConfig.culturalSymbols,
        colors: {
          primary: crestConfig.primaryColor,
          secondary: crestConfig.secondaryColor,
          accent: crestConfig.accentColor
        },
        style_preferences: {
          art_style: crestConfig.artStyle,
          complexity: crestConfig.complexity,
          background_style: crestConfig.backgroundStyle
        },
        include_spiritual: crestConfig.includeSpiritual,
        include_banner: crestConfig.includeBanner,
        include_stars: crestConfig.includeStars,
        include_border: crestConfig.includeBorder
      };

      setCrestConfig(prev => ({
        ...prev,
        generationStep: 'Generating your unique tribal crest with AI...'
      }));

      const response = await crestAPI.generateCrest(generationRequest);

      if (response.status === 'success' && response.crest_image) {
        setCrestConfig(prev => ({
          ...prev,
          generatedCrest: {
            imageUrl: response.crest_image,
            description: response.description,
            symbolism: response.symbolism,
            culturalSignificance: response.cultural_significance,
            id: response.crest_id
          },
          isGenerating: false,
          generationStep: ''
        }));

        toast({
          title: "Crest Generated Successfully!",
          description: "Your unique tribal crest has been created.",
        });
      } else {
        throw new Error(response.message || 'Failed to generate crest');
      }

    } catch (error) {
      console.error('Crest generation error:', error);
      setCrestConfig(prev => ({
        ...prev,
        isGenerating: false,
        generationStep: ''
      }));
      
      toast({
        variant: "destructive",
        title: "Generation Failed",
        description: error.message || "Failed to generate crest. Please try again.",
      });
    }
  };

  const downloadCrest = () => {
    if (crestConfig.generatedCrest?.imageUrl) {
      const link = document.createElement('a');
      link.href = crestConfig.generatedCrest.imageUrl;
      link.download = `${crestConfig.familyName.replace(/\s+/g, '_')}_Tribal_Crest.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const resetCrest = () => {
    setCrestConfig(prev => ({
      ...prev,
      generatedCrest: null,
      isGenerating: false,
      generationStep: ''
    }));
  };

  const saveToProfile = async () => {
    if (!crestConfig.generatedCrest?.imageUrl) {
      toast({
        variant: "destructive",
        title: "No Crest to Save",
        description: "Please generate a crest first.",
      });
      return;
    }

    try {
      // Create the description from the generated crest information
      const crestDescription = `${crestConfig.generatedCrest.description || ''}\n\nSymbolism: ${crestConfig.generatedCrest.symbolism || ''}\n\nCultural Significance: ${crestConfig.generatedCrest.culturalSignificance || ''}`.trim();

      // Update profile with the generated crest
      await publicProfileAPI.updateProfile({
        family_crest: crestConfig.generatedCrest.imageUrl,
        family_crest_description: crestDescription
      });

      toast({
        title: "Success!",
        description: "Your family crest has been saved to your profile.",
      });

    } catch (error) {
      console.error('Error saving crest to profile:', error);
      toast({
        variant: "destructive",
        title: "Save Failed",
        description: "Failed to save crest to profile. Please try again.",
      });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8">
              <Shield className="w-24 h-24 mx-auto mb-6 text-amber-600" />
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Tribal Crest Generator
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Create your unique ancestral family crest with AI-powered design
              </p>
            </div>

            <Card className="max-w-md mx-auto">
              <CardContent className="p-8">
                <Shield className="w-16 h-16 mx-auto mb-4 text-amber-600" />
                <h3 className="text-xl font-semibold mb-4">Access Sacred Crest Creation</h3>
                <p className="text-gray-600 mb-6">
                  Sign in to create your personalized tribal family crest with traditional symbols and cultural significance.
                </p>
                <AuthModal 
                  trigger={
                    <Button className="w-full">
                      Sign In for Crest Generation
                    </Button>
                  }
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }



  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <Shield className="w-16 h-16 mx-auto mb-4 text-amber-600" />
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Tribal Crest Generator
              </h1>
              <p className="text-xl text-gray-600">
                Design your unique ancestral family crest with traditional symbols and cultural significance
              </p>
            </div>

          {/* Generated Crest Display */}
          {crestConfig.generatedCrest && (
            <Card className="mb-8 bg-gradient-to-br from-amber-100 to-orange-100 border-2 border-amber-300">
              <CardHeader className="px-3 sm:px-6">
                <CardTitle className="flex items-center justify-center text-lg sm:text-xl">
                  <Crown className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-amber-600" />
                  Your Generated Tribal Crest
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center px-3 sm:px-6">
                <div className="mb-4 sm:mb-6">
                  <img 
                    src={crestConfig.generatedCrest.imageUrl} 
                    alt={`${crestConfig.familyName} Family Crest`}
                    className="w-full max-w-xs sm:max-w-sm md:max-w-md mx-auto rounded-lg shadow-lg border-4 border-amber-200"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <div className="bg-white p-3 sm:p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Description</h4>
                    <p className="text-gray-600 text-xs sm:text-sm">{crestConfig.generatedCrest.description}</p>
                  </div>
                  <div className="bg-white p-3 sm:p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Cultural Significance</h4>
                    <p className="text-gray-600 text-xs sm:text-sm">{crestConfig.generatedCrest.culturalSignificance}</p>
                  </div>
                </div>
                
                {crestConfig.generatedCrest.symbolism && (
                  <div className="bg-white p-3 sm:p-4 rounded-lg mb-4 sm:mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Symbolism</h4>
                    <p className="text-gray-600 text-xs sm:text-sm">{crestConfig.generatedCrest.symbolism}</p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row justify-center items-center gap-3 px-2">
                  <Button 
                    onClick={saveToProfile} 
                    className="flex items-center bg-green-600 hover:bg-green-700 w-full sm:w-auto text-sm"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Save to Profile
                  </Button>
                  <Button onClick={downloadCrest} className="flex items-center w-full sm:w-auto text-sm">
                    <Download className="w-4 h-4 mr-2" />
                    Download Crest
                  </Button>
                  <Button variant="outline" onClick={resetCrest} className="flex items-center w-full sm:w-auto text-sm">
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Create New Crest
                  </Button>
                </div>

                {/* Customization Disclaimer */}
                <div className="mt-6 sm:mt-8 bg-amber-50 border-2 border-amber-200 rounded-lg p-4 sm:p-6 mx-1 sm:mx-0">
                  <div className="flex items-start space-x-3 mb-4">
                    <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Important Notice</h4>
                      <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">
                        This Crest Design is an AI Generation that can only be Modified to a certain degree through this platform. 
                        If you would like further details and Modifications you can get your Crest Design Customized at the link Below.
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-center mt-4">
                    <Button 
                      onClick={() => window.open('https://iselflawammaster.com/product/family-crest-bundle-crest-ring-flag-2024/', '_blank')}
                      className="flex items-center bg-amber-600 hover:bg-amber-700 text-white text-sm w-full sm:w-auto"
                    >
                      <Wand2 className="w-4 h-4 mr-2" />
                      Get Custom Crest Design
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Crest Configuration Form */}
          {!crestConfig.generatedCrest && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Basic Information */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Users className="w-5 h-5 mr-2" />
                      Basic Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="familyName">Family Name *</Label>
                      <Input
                        id="familyName"
                        value={crestConfig.familyName}
                        onChange={(e) => setCrestConfig(prev => ({...prev, familyName: e.target.value}))}
                        placeholder="Enter your family name"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="motto">Family Motto</Label>
                      <Input
                        id="motto"
                        value={crestConfig.motto}
                        onChange={(e) => setCrestConfig(prev => ({...prev, motto: e.target.value}))}
                        placeholder="e.g., Honor, Courage, Unity"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="tribalAffiliation">Tribal Affiliation</Label>
                      <Input
                        id="tribalAffiliation"
                        value={crestConfig.tribalAffiliation}
                        onChange={(e) => setCrestConfig(prev => ({...prev, tribalAffiliation: e.target.value}))}
                        placeholder="Your tribal heritage"
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Crest Shape */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Shield className="w-5 h-5 mr-2" />
                      Crest Shape
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-3">
                      {shapeOptions.map((option) => {
                        const Icon = option.icon;
                        return (
                          <div
                            key={option.value}
                            className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                              crestConfig.shape === option.value
                                ? 'border-amber-500 bg-amber-50'
                                : 'border-gray-200 hover:border-amber-300'
                            }`}
                            onClick={() => setCrestConfig(prev => ({...prev, shape: option.value}))}
                          >
                            <div className="flex items-center">
                              <Icon className="w-5 h-5 mr-3 text-amber-600" />
                              <span className="font-medium">{option.label}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Colors */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Palette className="w-5 h-5 mr-2" />
                      Color Palette
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Primary Color</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {colorOptions.map((color) => (
                          <div
                            key={color.value}
                            className={`w-12 h-12 rounded-lg cursor-pointer border-2 transition-all ${
                              crestConfig.primaryColor === color.value
                                ? 'border-gray-800 scale-110'
                                : 'border-gray-300 hover:border-gray-500'
                            }`}
                            style={{ backgroundColor: color.value }}
                            onClick={() => setCrestConfig(prev => ({...prev, primaryColor: color.value}))}
                            title={`${color.label} - ${color.meaning}`}
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label>Secondary Color</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {colorOptions.map((color) => (
                          <div
                            key={color.value}
                            className={`w-12 h-12 rounded-lg cursor-pointer border-2 transition-all ${
                              crestConfig.secondaryColor === color.value
                                ? 'border-gray-800 scale-110'
                                : 'border-gray-300 hover:border-gray-500'
                            }`}
                            style={{ backgroundColor: color.value }}
                            onClick={() => setCrestConfig(prev => ({...prev, secondaryColor: color.value}))}
                            title={`${color.label} - ${color.meaning}`}
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <Label>Accent Color</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {colorOptions.map((color) => (
                          <div
                            key={color.value}
                            className={`w-12 h-12 rounded-lg cursor-pointer border-2 transition-all ${
                              crestConfig.accentColor === color.value
                                ? 'border-gray-800 scale-110'
                                : 'border-gray-300 hover:border-gray-500'
                            }`}
                            style={{ backgroundColor: color.value }}
                            onClick={() => setCrestConfig(prev => ({...prev, accentColor: color.value}))}
                            title={`${color.label} - ${color.meaning}`}
                          />
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Symbols */}
              <div className="space-y-6">
                {/* Primary Symbols */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Star className="w-5 h-5 mr-2" />
                      Primary Symbols *
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-3">
                      {primarySymbolOptions.map((symbol) => {
                        const Icon = symbol.icon;
                        const isSelected = crestConfig.primarySymbols.includes(symbol.value);
                        return (
                          <div
                            key={symbol.value}
                            className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                              isSelected
                                ? 'border-amber-500 bg-amber-50'
                                : 'border-gray-200 hover:border-amber-300'
                            }`}
                            onClick={() => handleSymbolToggle('primarySymbols', symbol.value)}
                          >
                            <div className="flex items-start">
                              <Icon className="w-5 h-5 mr-3 mt-1 text-amber-600" />
                              <div>
                                <div className="font-medium">{symbol.label}</div>
                                <div className="text-sm text-gray-600">{symbol.meaning}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Animals */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Bird className="w-5 h-5 mr-2" />
                      Animal Spirits
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2">
                      {animalOptions.map((animal) => {
                        const Icon = animal.icon;
                        const isSelected = crestConfig.animals.includes(animal.value);
                        return (
                          <div
                            key={animal.value}
                            className={`p-2 border rounded-lg cursor-pointer transition-colors text-center ${
                              isSelected
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 hover:border-green-300'
                            }`}
                            onClick={() => handleSymbolToggle('animals', animal.value)}
                            title={animal.meaning}
                          >
                            <Icon className="w-6 h-6 mx-auto mb-1 text-green-600" />
                            <div className="text-sm font-medium">{animal.label}</div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Plants */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <TreePine className="w-5 h-5 mr-2" />
                      Sacred Plants
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2">
                      {plantOptions.map((plant) => {
                        const Icon = plant.icon;
                        const isSelected = crestConfig.plants.includes(plant.value);
                        return (
                          <div
                            key={plant.value}
                            className={`p-2 border rounded-lg cursor-pointer transition-colors text-center ${
                              isSelected
                                ? 'border-green-500 bg-green-50'
                                : 'border-gray-200 hover:border-green-300'
                            }`}
                            onClick={() => handleSymbolToggle('plants', plant.value)}
                            title={plant.meaning}
                          >
                            <Icon className="w-6 h-6 mx-auto mb-1 text-green-600" />
                            <div className="text-sm font-medium">{plant.label}</div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Natural Elements */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Sun className="w-5 h-5 mr-2" />
                      Natural Elements
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2">
                      {naturalElementOptions.map((element) => {
                        const Icon = element.icon;
                        const isSelected = crestConfig.naturalElements.includes(element.value);
                        return (
                          <div
                            key={element.value}
                            className={`p-2 border rounded-lg cursor-pointer transition-colors text-center ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-blue-300'
                            }`}
                            onClick={() => handleSymbolToggle('naturalElements', element.value)}
                            title={element.meaning}
                          >
                            <Icon className="w-6 h-6 mx-auto mb-1 text-blue-600" />
                            <div className="text-sm font-medium">{element.label}</div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Cultural Symbols */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Feather className="w-5 h-5 mr-2" />
                      Cultural Symbols
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-2">
                      {culturalSymbolOptions.map((symbol) => {
                        const Icon = symbol.icon;
                        const isSelected = crestConfig.culturalSymbols.includes(symbol.value);
                        return (
                          <div
                            key={symbol.value}
                            className={`p-2 border rounded-lg cursor-pointer transition-colors ${
                              isSelected
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-300'
                            }`}
                            onClick={() => handleSymbolToggle('culturalSymbols', symbol.value)}
                          >
                            <div className="flex items-center">
                              <Icon className="w-5 h-5 mr-3 text-purple-600" />
                              <div>
                                <div className="font-medium">{symbol.label}</div>
                                <div className="text-sm text-gray-600">{symbol.meaning}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Style Preferences */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Palette className="w-5 h-5 mr-2" />
                      Style Preferences
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Art Style</Label>
                      <Select value={crestConfig.artStyle} onValueChange={(value) => setCrestConfig(prev => ({...prev, artStyle: value}))}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="traditional">Traditional</SelectItem>
                          <SelectItem value="modern">Modern</SelectItem>
                          <SelectItem value="minimalist">Minimalist</SelectItem>
                          <SelectItem value="detailed">Highly Detailed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label>Complexity Level</Label>
                      <Select value={crestConfig.complexity} onValueChange={(value) => setCrestConfig(prev => ({...prev, complexity: value}))}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="simple">Simple</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="complex">Complex</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="includeSpiritual"
                          checked={crestConfig.includeSpiritual}
                          onCheckedChange={(checked) => setCrestConfig(prev => ({...prev, includeSpiritual: checked}))}
                        />
                        <Label htmlFor="includeSpiritual">Include spiritual elements</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="includeBanner"
                          checked={crestConfig.includeBanner}
                          onCheckedChange={(checked) => setCrestConfig(prev => ({...prev, includeBanner: checked}))}
                        />
                        <Label htmlFor="includeBanner">Include banner with family name</Label>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="includeStars"
                          checked={crestConfig.includeStars}
                          onCheckedChange={(checked) => setCrestConfig(prev => ({...prev, includeStars: checked}))}
                        />
                        <Label htmlFor="includeStars">Include decorative stars</Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="includeBorder"
                          checked={crestConfig.includeBorder}
                          onCheckedChange={(checked) => setCrestConfig(prev => ({...prev, includeBorder: checked}))}
                        />
                        <Label htmlFor="includeBorder">Include decorative border</Label>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Generation Button */}
          {!crestConfig.generatedCrest && (
            <div className="mt-8 text-center">
              <Button
                onClick={generateCrest}
                disabled={crestConfig.isGenerating || !crestConfig.familyName.trim() || crestConfig.primarySymbols.length === 0}
                className="px-8 py-3 text-lg"
              >
                {crestConfig.isGenerating ? (
                  <>
                    <Wand2 className="w-5 h-5 mr-2 animate-spin" />
                    {crestConfig.generationStep}
                  </>
                ) : (
                  <>
                    <Wand2 className="w-5 h-5 mr-2" />
                    Generate Your Tribal Crest
                  </>
                )}
              </Button>
              
              {crestConfig.isGenerating && (
                <div className="mt-4 text-center">
                  <div className="inline-flex items-center px-4 py-2 bg-amber-100 text-amber-800 rounded-lg">
                    <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
                    Creating your unique crest with AI...
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrestGenerator;